import React, { useState, useRef, useEffect } from 'react';
import moment from 'moment';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Tooltip,
  Avatar,
  Divider,
  Button,
  CircularProgress,
  IconButton
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import {
  Login as LoginIcon,
  Logout as LogoutIcon,
  AddCircle as CreateIcon,
  Edit as UpdateIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CloudUpload as UploadIcon,
  Code as RendererActionIcon,
  Person as UserIcon,
  Movie as MovieIcon,
  ViewInAr as ViewInArIcon,
  InsertDriveFile as AssetIcon,
  Business as OrganizationIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  Article as DocumentIcon,
  Folder as FolderIcon,
  Code as CodeIcon,
  Info as InfoIcon,
  Today as TodayIcon,
  CalendarMonth as CalendarIcon,
  Add as AddIcon,
  MoreTime as MoreTimeIcon,
} from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const activityIcons = {
  LOGIN: <LoginIcon />,
  LOGOUT: <LogoutIcon />,
  CREATE: <CreateIcon />,
  UPDATE: <UpdateIcon />,
  DELETE: <DeleteIcon />,
  VIEW: <ViewIcon />,
  UPLOAD: <UploadIcon />,
  RENDERER_ACTION: <RendererActionIcon />
};

const resourceIcons = { user: <UserIcon />, scene: <MovieIcon />, renderer: <ViewInArIcon />, asset: <AssetIcon />, organization: <OrganizationIcon /> };
const getFileTypeIcon = (filename) => {
  if (!filename) return <AssetIcon />;

  const extension = filename.split('.').pop().toLowerCase();
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'];
  const videoExtensions = ['mp4', 'webm', 'avi', 'mov', 'wmv', 'flv', 'mkv'];
  const documentExtensions = ['pdf', 'doc', 'docx', 'txt', 'csv', 'xls', 'xlsx', 'ppt', 'pptx'];
  const codeExtensions = ['js', 'jsx', 'ts', 'tsx', 'html', 'css', 'json', 'xml', 'py', 'java', 'c', 'cpp'];

  if (imageExtensions.includes(extension)) return <ImageIcon />;
  if (videoExtensions.includes(extension)) return <VideoIcon />;
  if (documentExtensions.includes(extension)) return <DocumentIcon />;
  if (codeExtensions.includes(extension)) return <CodeIcon />;

  return <AssetIcon />;
};
const parseResourcePath = (resourceId) => {
  if (!resourceId || typeof resourceId !== 'string') return { sceneName: '', folderPath: '', fileName: '' };

  const parts = resourceId.split('/');

  if (parts.length < 3) return { sceneName: '', folderPath: '', fileName: resourceId };

  if (parts[0] === 'scenes') {
    const sceneName = parts[1];
    const fileName = parts[parts.length - 1];
    const folderPath = parts.slice(2, parts.length - 1).join('/');

    return { sceneName, folderPath, fileName };
  }

  return {
    sceneName: '',
    folderPath: resourceId.substring(0, resourceId.lastIndexOf('/')),
    fileName: resourceId.substring(resourceId.lastIndexOf('/') + 1)
  };
};

// OPUS-913: semantic action tones (design ACTION_TONE) — direct token hexes so
// CREATE/UPDATE don't render as the theme's neutral-grey primary/secondary.
const ACTION_HEX = {
  LOGIN: '#58C4FF',          // blue
  LOGOUT: '#808080',         // dim
  CREATE: '#45FFA7',         // green
  UPDATE: '#C445FF',         // violet
  DELETE: '#FF455C',         // coral
  VIEW: '#58C4FF',           // blue
  UPLOAD: '#45FFA7',         // green
  RENDERER_ACTION: '#FCC144', // amber
};
const getActivityHex = (activityType) => ACTION_HEX[activityType] || '#808080';

const EnhancedTimelineItem = ({ time, icon, colorHex = '#808080', children, isFirst, isLast }) => {
  return (
    <Box sx={{
      display: 'flex',
      mb: 2, 
      position: 'relative',
      '&:hover': {
        '& .timeline-dot': {
          transform: 'scale(1.1)',
          boxShadow: 2
        },
        '& .timeline-content': {
          transform: 'translateX(0)',
          boxShadow: 2
        }
      }
    }}>
      <Box sx={{
        width: '80px',
        textAlign: 'right',
        pr: 1.5, 
        pt: 0.5,
        opacity: 0.8
      }}>
        <Typography
          variant="caption" 
          color="text.secondary"
          sx={{
            fontWeight: 500,
            fontSize: '0.75rem',
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.7),
            px: 0.75, 
            py: 0.25,
            borderRadius: 1,
            display: 'inline-block'
          }}
        >
          {time}
        </Typography>
      </Box>

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        px: 0.75,
        minWidth: '32px' 
      }}>
        <Box
          sx={{
            position: 'absolute',
            top: isFirst ? '50%' : 0,
            bottom: isLast ? '50%' : 0,
            width: '2px',
            borderRadius: '2px',
            bgcolor: `${colorHex}33`,
            zIndex: 0,
            left: '50%',
            transform: 'translateX(-50%)'
          }}
        />

        <Avatar
          className="timeline-dot"
          sx={{
            bgcolor: `${colorHex}22`,
            color: colorHex,
            zIndex: 1,
            width: 32,
            height: 32,
            border: '2px solid var(--surface-1)',
            boxShadow: 1,
            transition: 'all 0.2s ease',
            '& .MuiSvgIcon-root': {
              fontSize: '1.25rem'
            }
          }}
        >
          {icon}
        </Avatar>
      </Box>

      <Box
        className="timeline-content"
        sx={{
          flex: 1,
          pl: 1.5, 
          transition: 'all 0.2s ease',
          transform: 'translateX(3px)', 
          maxWidth: 'calc(100% - 105px)' 
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

const ActivityTimeline = ({ 
  activities = [], 
  onLoadMore, 
  hasMore = false,
  hasMoreActivities = false,
  loadingMore = false, 
  loading = false,
  totalItems = 0 
}) => {
  const [expandedDate, setExpandedDate] = useState(null);
  const scrollContainerRef = useRef(null);
  // OPUS-913: explicit "Load more" button (scroll-based auto-load removed per request).
  const shouldShowLoadMore = hasMore || hasMoreActivities;

  const groupedActivities = activities.reduce((groups, activity) => {
    const date = moment(activity.timestamp).format('YYYY-MM-DD');
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(activity);
    return groups;
  }, {});

  const dateGroups = Object.keys(groupedActivities).sort((a, b) => moment(b).diff(moment(a)));
  const today = moment().format('YYYY-MM-DD');

  const toggleDateExpansion = (date) => {
    setExpandedDate(expandedDate === date ? null : date);
  };

  return (
    <Box 
      ref={scrollContainerRef}
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'auto',
        py: 0,
        px: { xs: 1, sm: 2, md: 3 },
        position: 'relative'
      }}
    >
      {dateGroups.map((date, dateIndex) => {
        const isToday = date === today;
        const sortedActivities = [...groupedActivities[date]].sort((a, b) =>
          moment(b.timestamp).diff(moment(a.timestamp))
        );

        return (
          <Box key={date} sx={{ mb: 4 }}>
            <Box
              onClick={() => toggleDateExpansion(date)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 3,
                px: 2,
                py: 1,
                bgcolor: (theme) => isToday
                  ? alpha(theme.palette.success.main, 0.05)
                  : alpha(theme.palette.primary.main, 0.03),
                borderRadius: 2,
                cursor: 'pointer',
                border: (theme) => `1px solid ${isToday
                  ? alpha(theme.palette.success.main, 0.2)
                  : alpha(theme.palette.primary.main, 0.1)
                  }`,
                '&:hover': {
                  bgcolor: (theme) => isToday
                    ? alpha(theme.palette.success.main, 0.1)
                    : alpha(theme.palette.primary.main, 0.08)
                }
              }}
            >
              {isToday ?
                <TodayIcon sx={{ mr: 1.5, color: 'success.main' }} /> :
                <CalendarIcon sx={{ mr: 1.5, color: 'text.secondary' }} />
              }
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: isToday ? 'success.main' : 'text.primary'
                }}
              >
                {isToday ? 'Today' : moment(date).format('MMMM D, YYYY')}
              </Typography>
              {isToday && (
                <Chip
                  label="Today"
                  size="small"
                  color="success"
                  sx={{ ml: 1.5, height: 20, fontSize: '0.75rem' }}
                />
              )}
            </Box>

            <Box sx={{ pl: { xs: 0, md: 1 } }}>
              {sortedActivities.map((activity, index) => {
                const activityHex = getActivityHex(activity.activity_type);
                const isAsset = activity.resource_type === 'asset';

                const { sceneName, folderPath } = isAsset
                  ? parseResourcePath(activity.resource_id)
                  : { sceneName: '', folderPath: '' };

                const icon = activityIcons[activity.activity_type] || <InfoIcon />;
                const time = moment(activity.timestamp).format('h:mm a');

                return (
                  <EnhancedTimelineItem
                    key={activity._id}
                    time={time}
                    icon={icon}
                    colorHex={activityHex}
                    isFirst={index === 0}
                    isLast={index === sortedActivities.length - 1}
                  >
                    <Paper
                      elevation={1}
                      sx={{
                        p: 1.75,
                        bgcolor: 'var(--surface-0)',
                        borderLeft: 3,
                        borderColor: activityHex,
                        borderRadius: '6px',
                        transition: 'all 0.2s',
                        overflow: 'hidden',
                        position: 'relative',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 2
                        }
                      }}
                    >
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1 
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight="bold" sx={{ mr: 1 }}>
                            {activity.user_name}
                          </Typography>
                          <Chip
                            icon={icon}
                            label={activity.activity_type}
                            size="small"
                            sx={{
                              height: 20,
                              bgcolor: `${activityHex}22`,
                              color: activityHex,
                              border: `1px solid ${activityHex}3a`,
                              fontWeight: 600,
                              '& .MuiChip-label': { px: 1, fontSize: '0.75rem' },
                              '& .MuiChip-icon': { fontSize: '0.875rem', ml: 0.5, mr: -0.25, color: activityHex }
                            }}
                          />
                        
                        </Box>
                      </Box>

                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                        pb: 0.75,
                        borderBottom: '1px solid',
                        borderColor: (theme) => alpha(theme.palette.divider, 0.3)
                      }}>
                        <Avatar
                          sx={{
                            bgcolor: (theme) => alpha(theme.palette.background.default, 0.7),
                            color: 'text.primary',
                            width: 24, 
                            height: 24, 
                            '& .MuiSvgIcon-root': { fontSize: '0.875rem' }
                            }}
                            
                            >
                            {isAsset
                            ? getFileTypeIcon(activity.resource_name)
                            : resourceIcons[activity.resource_type] || <AssetIcon />
                            }
                            </Avatar>
                            <Box>
                             
                            <Typography variant="body2" fontWeight="medium" sx={{ lineHeight: 1.1 }}>
                            {activity.resource_name || activity.resource_type}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem' }}>
                            {activity.resource_type.charAt(0).toUpperCase() + activity.resource_type.slice(1)}
                            {activity._id && (
                              <>
                              <span style={{ margin: '0 4px' }}>•</span>
                              <span>ID: {activity._id}</span>
                              <Tooltip title="Copy Log ID">
                              <IconButton 
                                size="small" 
                                onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(activity._id);
                                }}
                                sx={{ 
                                ml: 0.5, 
                                p: 0.25,
                                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.08)' }
                                }}
                              >
                                <ContentCopyIcon sx={{ fontSize: '0.75rem' }} />
                              </IconButton>
                              </Tooltip>
                              </>
                            )}
                            </Typography>
                            </Box>
                            </Box>
                            
                          {activity.details && (
                          <Box sx={{ 
                            mt: 0.5, 
                            px: 1, 
                            py: 0.75,
                            bgcolor: (theme) => alpha(theme.palette.background.default, 0.5),
                            borderRadius: 1,
                            fontSize: '0.875rem'
                          }}>
                            <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {activity.details}
                            </Typography>
                          </Box>
                          )}

                          {isAsset && (sceneName || folderPath) && (
                          <Box sx={{
                            display: 'flex',
                            flexDirection: 'row', 
                          flexWrap: 'wrap',
                          gap: 1,
                          mt: 0.5,
                          fontSize: '0.875rem'
                        }}>
                          {sceneName && (
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              bgcolor: (theme) => alpha(theme.palette.info.light, 0.1),
                              border: '1px solid',
                              borderColor: (theme) => alpha(theme.palette.info.light, 0.2),
                              borderRadius: 1,
                              px: 0.75,
                              py: 0.25
                            }}>
                              <MovieIcon sx={{ fontSize: '0.875rem', color: 'info.main' }} />
                              <Typography variant="caption">
                                {sceneName}
                              </Typography>
                            </Box>
                          )}

                          {folderPath && (
                            <Tooltip title={folderPath} arrow>
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                bgcolor: (theme) => alpha(theme.palette.warning.light, 0.1),
                                border: '1px solid',
                                borderColor: (theme) => alpha(theme.palette.warning.light, 0.2),
                                borderRadius: 1,
                                px: 0.75,
                                py: 0.25,
                                maxWidth: '100%',
                                overflow: 'hidden'
                              }}>
                                <FolderIcon sx={{ fontSize: '0.875rem', color: 'warning.main' }} />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    maxWidth: '120px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {folderPath}
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      )}

                      {activity.ip_address && (
                        <Box sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          opacity: 0.7,
                          '&:hover': { opacity: 1 }
                        }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono)' }}>
                            IP: {String(activity.ip_address).replace(/^::ffff:/, "").replace(/^::1$/, "localhost")}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </EnhancedTimelineItem>
                );
              })}
            </Box>
          </Box>
        );
      })}

      {activities.length === 0 && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8
        }}>
          <MoreTimeIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.6, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No activity data to display
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Activities will appear here once they are recorded
          </Typography>
        </Box>
      )}

      {shouldShowLoadMore && activities.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1, pb: 1 }}>
          <Box
            component="button"
            type="button"
            disabled={loadingMore}
            onClick={onLoadMore}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1, px: 2.5, height: 38,
              borderRadius: 'var(--r-md)', cursor: loadingMore ? 'default' : 'pointer',
              background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)',
              fontSize: 13.5, fontWeight: 600, fontFamily: 'var(--font-sans)',
              opacity: loadingMore ? 0.6 : 1, transition: 'background .14s, border-color .14s',
              '&:hover': { background: 'var(--surface-3)', borderColor: 'var(--border-strong)' },
            }}
          >
            {loadingMore ? <CircularProgress size={16} sx={{ color: 'var(--text-muted)' }} /> : <AddIcon sx={{ fontSize: 18 }} />}
            {loadingMore ? 'Loading…' : 'Load more activities'}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ActivityTimeline;